import { configureStore, combineReducers, Action } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import authReducer from '../reducers/authSlice';
import vehicleReducer from '../reducers/vehicleSlice';
import vehiclePhotosReducer from '../reducers/vehiclePhotosSlice';
import damageLocationsReducer from '../reducers/damageLocationsSlice';
import damagedPartsReducer from '../reducers/damagedPartsSlice';
import incidentDetailsReducer from '../reducers/incidentDetailsSlice';
import operatorInfoReducer from '../reducers/operatorInfoSlice';
import historyReducer from '../reducers/historySlice';
import navigationReducer from '../reducers/navigationSlice';
import { persistConfig } from './persistConfig';

const appReducer = combineReducers({
  auth: authReducer,
  vehicle: vehicleReducer,
  vehiclePhotos: vehiclePhotosReducer,
  damageLocations: damageLocationsReducer,
  damagedParts: damagedPartsReducer,
  incidentDetails: incidentDetailsReducer,
  operatorInfo: operatorInfoReducer,
  history: historyReducer,
  navigation: navigationReducer
});

type AppState = ReturnType<typeof appReducer>;

const rootReducer = (state: AppState | undefined, action: Action): AppState => {
  if (action.type === 'RESET_INCIDENT') {
    const preservedState = state ? { auth: state.auth, history: state.history } : {};
    return appReducer(preservedState as AppState | undefined, action);
  }
  return appReducer(state, action);
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'auth/login/fulfilled',
          'incidentDetails/setIncidentDate'
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.timestamp', 'meta.arg', 'register', 'rehydrate'],
        // Ignore these paths in the state
        ignoredPaths: ['incidentDetails.incidentDate', '_persist']
      }
    }),
  devTools: process.env.NODE_ENV !== 'production'
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Create persistor
export const persistor = persistStore(store);

export const resetIncident = () => ({ type: 'RESET_INCIDENT' } as const);

export default store;